import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	ExcelExport,
	ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { useTranslation } from "react-i18next";

import {
	AccountStatementFilter,
	getAccountStatementApi,
	getAccountStatementPeriodsApi,
} from "api/accounting";
import { AccountStatement as AccountStatementItem } from "interface/accounting";
import styles from "styles.module.css";

import "../salesNote/styles.css";
import AccountStatementMobileList from "./AccountStatementMobileList";
import Filter from "./Filter";
import { accountStatementGridColumns } from "./GridColumns";
import { useUserOrganizationsStore } from "store/user";
import GridWithGroupPaging from "ui/grid/GridWithGroupPaging";
import { OrganizationType } from "interface/user";

const AccountStatement = () => {
	const { t } = useTranslation();
	const now = new Date();

	const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
	const [mobileData, setMobileData] = useState<AccountStatementItem[]>([]);
	const [mobileLoading, setMobileLoading] = useState(false);

	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);
	const mobileExcelRef = useRef<ExcelExport>(null);
	const [periods, setPeriods] = useState<number[]>([]);
	const { defaultOrg } = useUserOrganizationsStore();

	const [filterOptions, setFilterOptions] = useState<AccountStatementFilter>({
		buyerLedgerAccount: defaultOrg
			? (defaultOrg.ledgerAccountNumber as any)
			: 0,
		buyerName: defaultOrg?.displayName,
		year: now.getFullYear(),
		monthFrom: 1, // January
		monthTo: now.getMonth() + 1,
	});

	useEffect(() => {
		const updateView = () => setIsMobileView(window.innerWidth < 768);
		window.addEventListener("resize", updateView);

		return () => window.removeEventListener("resize", updateView);
	}, []);

	useEffect(() => {
		const { buyerLedgerAccount, year } = filterOptions;
		if (buyerLedgerAccount === 0) {
			setPeriods([]);
			return;
		}

		let active = true;
		getAccountStatementPeriodsApi(buyerLedgerAccount, year)
			.then((response) => {
				if (!active) return;

				const availablePeriods = Array.from(new Set(response.data || [])).sort(
					(a, b) => a - b,
				);
				setPeriods(availablePeriods);
				setFilterOptions((currentFilter) => ({
					...currentFilter,
					monthFrom: availablePeriods[0],
					monthTo: availablePeriods[availablePeriods.length - 1],
				}));
			})
			.catch((error) => {
				if (active) console.error("error in getting period", error);
			});

		return () => {
			active = false;
		};
	}, [filterOptions.buyerLedgerAccount, filterOptions.year]);

	useEffect(() => {
		if (filterOptions.buyerLedgerAccount !== 0) {
			return;
		}
		if (
			!defaultOrg ||
			!defaultOrg.ledgerAccountNumber ||
			defaultOrg.type !== OrganizationType.Buyer
		) {
			return;
		}

		setFilterOptions((filter) => ({
			...filter,
			buyerLedgerAccount: defaultOrg.ledgerAccountNumber as any,
			buyerName: defaultOrg.displayName,
		}));
	}, [defaultOrg?.ledgerAccountNumber]);

	useEffect(() => {
		// if (!isMobileView) return;
		let active = true;
		setMobileLoading(true);
		const { buyerLedgerAccount } = filterOptions;
		if (buyerLedgerAccount === 0) {
			setPeriods([]);
			return;
		}
		getAccountStatementApi(filterOptions)
			.then((response) => {
				if (active) setMobileData(response.data || []);
			})
			.finally(() => {
				if (active) setMobileLoading(false);
			});
		return () => {
			active = false;
		};
	}, [filterOptions, isMobileView]);

	return (
		<div className={styles.PageContainer}>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>
					{t("AccountStatement")}
				</legend>
			</div>
			<ExcelExport
				ref={mobileExcelRef}
				data={mobileData.map((item) => ({
					...item,
					voucherDate: new Date(item.voucherDate),
					dueDate: item.dueDate ? new Date(item.dueDate) : null,
					paidDate: item.paidDate ? new Date(item.paidDate) : null,
				}))}
				fileName="account_statement.xlsx"
			>
				<ExcelExportColumn field="voucherNumber" title={t("VoucherNumber")} />
				<ExcelExportColumn
					field="voucherDate"
					title={t("VoucherDate")}
					cellOptions={{ format: "dd.mm.yyyy" }}
				/>
				<ExcelExportColumn field="voucherType" title={t("VoucherType")} />
				<ExcelExportColumn field="accountingYear" title={t("Year")} />
				<ExcelExportColumn field="accountingPeriod" title={t("Period")} />
				<ExcelExportColumn field="amount" title={t("Amount")} />
				<ExcelExportColumn
					field="remainingAmount"
					title={t("RemainingAmount")}
				/>
				<ExcelExportColumn
					field="dueDate"
					title={t("DueDate")}
					cellOptions={{ format: "dd.mm.yyyy" }}
				/>
				<ExcelExportColumn
					field="paidDate"
					title={t("PaidDate")}
					cellOptions={{ format: "dd.mm.yyyy" }}
				/>
				<ExcelExportColumn field="invoiceNumber" title={t("InvoiceNumber")} />
				<ExcelExportColumn
					field="crossReferenceNumber"
					title={t("CrossReferenceNumber")}
				/>
				<ExcelExportColumn
					field="balanceAfterPeriod"
					title={t("BalanceAfterPeriod")}
				/>
			</ExcelExport>
			<Filter
				filterOptions={filterOptions}
				setFilterOptions={setFilterOptions}
				periods={periods}
				exportAsExcel={() =>
					isMobileView
						? mobileExcelRef.current?.save()
						: excelRef.current?.save()
				}
			/>
			<div style={{ marginTop: 30 }}>
				{isMobileView ? (
					<AccountStatementMobileList
						items={mobileData}
						loading={mobileLoading}
						periods={periods}
					/>
				) : (
					<GridWithGroupPaging
						gridColumns={accountStatementGridColumns(
							mobileData.length > 0 ? mobileData[0] : null,
							periods,
						)}
						filter={filterOptions}
						gridData={mobileData}
						// apiMethod={getAccountStatementApi}
						// getloadedData={handleLoadedData}
						exportGridAsPDFRef={pdfRef}
						exportGridAsExcelRef={excelRef}
						// showDetailIcon={false}
						// lastColWidth="0px"
						filterable={true}
						groupable={false}
						pageable={false}
						itemAboveHeight={50}
						sortable={{ mode: "single", allowUnsort: true }}
						excelProps={{ fileName: "account_statement.xlsx" }}
					/>
				)}
			</div>
		</div>
	);
};

export default AccountStatement;
